import React, { useEffect, useRef, useState } from 'react';
import { CylinderContainer, Column, CardDiv } from './CylinderCardBackground.styles';

const CylinderCardBackground = ({
  numColumns = 9,
  cardsPerColumn = 8,
  radius = 480,
  arcDegrees = 160,
  columnWidth = 160,
  cardHeight = 224,
  speed = 24,
  imageUrls = [],
  opacity = 0.55,
  perspective = 1000,
  className = ''
}) => {
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const lastTimeRef = useRef(0);
  const columnOffsetsRef = useRef([]);
  const [cardImages, setCardImages] = useState([]);
  const [screenSize, setScreenSize] = useState({ width: 0, height: 0 });
  const [responsiveColumns, setResponsiveColumns] = useState(numColumns);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      const newScreenSize = {
        width: window.innerWidth,
        height: window.innerHeight
      };
      setScreenSize(newScreenSize);

      // Calculate responsive number of columns
      const minColumns = 3;
      const maxColumns = numColumns;
      const targetCardWidth = columnWidth;
      const availableWidth = newScreenSize.width * 0.9; // 90% of screen width
      const calculatedColumns = Math.floor(availableWidth / targetCardWidth);
      
      // Clamp between min and max columns
      const newColumnCount = Math.max(minColumns, Math.min(maxColumns, calculatedColumns));
      setResponsiveColumns(newColumnCount);
    };

    // Set initial size
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [numColumns, columnWidth]);

  // Initialize column offsets
  useEffect(() => {
    columnOffsetsRef.current = Array(responsiveColumns).fill(0);
  }, [responsiveColumns]);

  // Generate card data (triple for seamless infinite loop)
  useEffect(() => {
    const totalCards = cardsPerColumn * 3; // Triple for seamless infinite loop
    const cards = [];
    
    for (let i = 0; i < totalCards; i++) {
      cards.push({
        id: i
      });
    }
    
    setCardImages(cards);
  }, [cardsPerColumn]);

  // Animation loop
  useEffect(() => {
    const animate = (currentTime) => {
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = currentTime;
      }
      
      const deltaTime = (currentTime - lastTimeRef.current) / 1000; // Convert to seconds
      lastTimeRef.current = currentTime;

      // Calculate responsive card height for proper tile height
      let responsiveColumnWidth, responsiveCardHeight;
      
      if (responsiveColumns <= 3) {
        // Scale the last 3 columns proportionally
        const availableWidth = screenSize.width * 0.9;
        const spacing = availableWidth * 0.1;
        const cardArea = availableWidth - spacing;
        responsiveColumnWidth = cardArea / responsiveColumns;
        const tradingCardRatio = 2.5 / 3.5;
        responsiveCardHeight = responsiveColumnWidth / tradingCardRatio;
      } else {
        // Maintain consistent card size
        responsiveColumnWidth = columnWidth;
        const tradingCardRatio = 2.5 / 3.5;
        responsiveCardHeight = responsiveColumnWidth / tradingCardRatio;
      }

      // Update each column's offset
      columnOffsetsRef.current = columnOffsetsRef.current.map((offset, index) => {
        const direction = index % 2 === 0 ? 1 : -1; // Even columns up, odd columns down
        const newOffset = offset + (direction * speed * deltaTime);
        
        // Wrap around for seamless infinite looping
        const tileHeight = cardsPerColumn * (responsiveCardHeight + 20);
        return ((newOffset % tileHeight) + tileHeight) % tileHeight;
      });

      // Update DOM
      if (containerRef.current) {
        const columns = containerRef.current.querySelectorAll('[data-column]');
        columns.forEach((column, index) => {
          const offset = columnOffsetsRef.current[index];
          column.style.transform = `translateX(-50%) translateY(-50%) translate3d(0, ${-offset}px, 0)`;
        });
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [speed, cardsPerColumn, cardHeight, columnWidth, responsiveColumns, screenSize]);

  return (
    <CylinderContainer
      ref={containerRef}
      className={className}
      $perspective={perspective}
      $opacity={opacity}
      aria-hidden="true"
    >
      {Array.from({ length: responsiveColumns }, (_, i) => {
        // Calculate card dimensions first
        let responsiveColumnWidth, responsiveCardHeight;
        
        if (responsiveColumns <= 3) {
          // Scale the last 3 columns proportionally
          const availableWidth = screenSize.width * 0.9;
          const spacing = availableWidth * 0.1; // 10% for spacing
          const cardArea = availableWidth - spacing;
          responsiveColumnWidth = cardArea / responsiveColumns;
          const tradingCardRatio = 2.5 / 3.5;
          responsiveCardHeight = responsiveColumnWidth / tradingCardRatio;
        } else {
          // Maintain consistent card size
          responsiveColumnWidth = columnWidth;
          const tradingCardRatio = 2.5 / 3.5;
          responsiveCardHeight = responsiveColumnWidth / tradingCardRatio;
        }
        
        // Calculate even spacing that matches card gap (20px) and centers columns
        const cardGap = 20; // Same as margin-bottom in CardDiv
        const totalCardWidth = responsiveColumnWidth;
        const totalSpacing = (responsiveColumns - 1) * cardGap;
        const totalWidth = (responsiveColumns * totalCardWidth) + totalSpacing;
        const startOffset = (screenSize.width - totalWidth) / 2;
        const columnPosition = startOffset + (i * (totalCardWidth + cardGap)) + (totalCardWidth / 2);
        const x = (columnPosition / screenSize.width) * 100;

        return (
          <Column
            key={i}
            data-column={i}
            $x={x}
            $width={responsiveColumnWidth}
            $height={cardsPerColumn * (responsiveCardHeight + 20) * 3} // Triple height for infinite loop
          >
            {cardImages.map((card) => (
              <CardDiv
                key={card.id}
                $width={responsiveColumnWidth}
                $height={responsiveCardHeight}
              />
            ))}
          </Column>
        );
      })}
    </CylinderContainer>
  );
};

export default CylinderCardBackground;
